/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { Menu, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AlertsMenu, Box, FuseIcon, List, ListItem, MenuItem, ProfileMenu } from '/src/components';
import { useHeaderStore, useMenuStore, useRootStore, useUserStore } from '/src/context';
import { TMenuItem } from '/src/lib/types';

export const Header: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const menuStore = useMenuStore();
  const headerStore = useHeaderStore();
  const rootStore = useRootStore();
  const location = useLocation();
  const navigate = useNavigate();

  /** Computed **/
  const currentPage = location.pathname.slice(1);
  const shouldRender = userStore.user?.roles && menuStore.shouldRender(userStore.user);
  const menu = menuStore.getMenu('topbar');

  const isViewStatement = useMatch('/statements/:loanId/:month');
  const isViewLenderStatement = useMatch('/statements/investors/:lenderId/:month');
  const renderHeader = !isViewStatement && !isViewLenderStatement;

  /** Methods **/

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, item: TMenuItem) => {
    item.anchor = event.currentTarget;
    item.open = true;
  };

  const handleClose = (item: TMenuItem) => {
    item.anchor = null;
    item.open = false;
  };

  /** Effects **/
  useEffect(() => {
    // Init menu items
    rootStore.adjustmentStore.initMenuItems();
    rootStore.advanceStore.initMenuItems();
    rootStore.assetStore.initMenuItems();
    rootStore.companyStore.initMenuItems();
    rootStore.lenderStore.initMenuItems();
    rootStore.loanStore.initMenuItems();
    rootStore.participationStore.initMenuItems();
    rootStore.paymentStore.initMenuItems();
    rootStore.statementStore.initMenuItems();
    rootStore.transactionStore.initMenuItems();

    // Get data
    Promise.all([
      rootStore.alertStore.query(),
      headerStore.loadCompanies(),
      headerStore.loadLoans(),
      headerStore.loadLenders(),
    ]);

    // Begin polling for alerts
    const alertPoll = setInterval(() => {
      rootStore.alertStore.tick();
    }, rootStore.alertStore.pollTimer);

    return () => clearInterval(alertPoll);
  }, []);

  useEffect(() => {
    if (!userStore.user && !location.pathname.includes('signin') && !location.pathname.includes('password'))
      navigate('/signin');
  }, [userStore.user, location.pathname]);

  /** Render **/
  return (
    <section>
      {renderHeader && (
        <Box>
          <Box component="nav" layout-align="start center" display="flex">
            {!userStore.user && (
              <Box style={{ padding: '0 15px' }}>
                <FuseIcon height="36px" width="36px" />
              </Box>
            )}

            {/* MAIN NAVBAR */}
            {shouldRender && (
              <List
                component="ul"
                sx={{ display: 'flex', lineHeight: '70px' }}
                layout-align="start center"
                className="left"
              >
                {/* SCALE LOGO */}
                <ListItem className="left">
                  <Tooltip title="Dashboard">
                    <Link to="/">
                      <FuseIcon height="36px" width="36px" />
                    </Link>
                  </Tooltip>
                </ListItem>

                {/* ALERTS */}
                <ListItem className="left">
                  <AlertsMenu />
                </ListItem>

                {/* MENU ITEMS */}
                {userStore.user &&
                  menu.items
                    .filter((item) => item.shouldRender(userStore.user))
                    .toSorted((a, b) => a.position - b.position)
                    .map((item, itemIndex) => (
                      <ListItem
                        key={`${item.title}-${itemIndex}`}
                        className={`ng-scope ${currentPage === item.link ? 'nav-selected' : ''}`}
                        sx={{ height: '70px', lineHeight: '70px' }}
                      >
                        {item.items.length === 0 && (
                          <Link className="ng-scope" to={`/${item.link}`} aria-label={item.title}>
                            <Box component="span" className="ng-binding">
                              {item.title}
                            </Box>
                          </Link>
                        )}
                        {!!item.items.length && (
                          <Box className="md-menu ng-scope">
                            <a
                              id={`${item.title}-${itemIndex}`}
                              aria-controls={item.open ? 'basic-menu' : undefined}
                              aria-haspopup="true"
                              aria-expanded={item.open ? 'true' : undefined}
                              onClick={(e) => handleClick(e, item)}
                            >
                              <Box component="span" className="ng-binding">
                                {item.title}
                              </Box>
                            </a>
                          </Box>
                        )}
                        {!!item.items.length && (
                          <Menu
                            className="md-menu"
                            anchorEl={item.anchor}
                            open={item.open}
                            onClose={() => handleClose(item)}
                            slotProps={{
                              list: {
                                'aria-labelledby': `${item.title}-${itemIndex}`,
                              },
                            }}
                          >
                            <Box className={'md-menu-content[width="4"] md-default-theme'} minWidth="256px">
                              {item.items
                                .toSorted((a, b) => b.position - a.position)
                                .filter((subItem) => subItem.shouldRender(userStore.user))
                                .map((subItem: Record<string, any>, subItemIndex) => (
                                  <MenuItem
                                    className="md-menu-item"
                                    key={`${subItem.title}-${subItemIndex}`}
                                    onClick={() => handleClose(item)}
                                    selected={location.pathname === subItem.link}
                                  >
                                    <button
                                      className="md-button md-default-theme"
                                      onClick={() => navigate(`/${subItem.link}`)}
                                      aria-label={subItem.title}
                                    >
                                      <Box component="span">{subItem.title}</Box>
                                    </button>
                                  </MenuItem>
                                ))}
                            </Box>
                          </Menu>
                        )}
                      </ListItem>
                    ))}
              </List>
            )}

            <Box sx={{ flex: 1 }} />

            {/* USER MENU */}
            {userStore.user && <ProfileMenu />}

            {!userStore.user && (
              <List component="ul">
                <ListItem>
                  <Link to="/signin" aria-label="Sign In">
                    <Box>Sign In</Box>
                  </Link>
                </ListItem>
              </List>
            )}
          </Box>
        </Box>
      )}
    </section>
  );

  // TODO: re-implement mobile menu:

  //   {/* <md-sidenav className="md-sidenav-left md-whiteframe-z2" md-component-id="left" md-is-locked-open="scope.isOpen"> */}
  // <Box className="md-sidenav-left md-whiteframe-z2" md-component-id="left" md-is-locked-open="scope.isOpen">
  //   <Toolbar className="">
  //     <h1 className="md-toolbar-tools">
  //       <Tooltip title="Close">
  //         <IconButton className="md-icon-button" onClick={() => toggleMenu()}>
  //           <ChevronLeft />
  //         </IconButton>
  //       </Tooltip>
  //     </h1>
  //   </Toolbar>
  //   <Box sx={{ paddingBottom: '20px' }}>
  //     {menu.items
  //       .toSorted((a, b) => (a.position > b.position ? 1 : -1))
  //       .map((item) => (
  //         <ListItem
  //           className="hoverable black-text"
  //           onClick={() => openPage(`/${item.link}`)}
  //           aria-label={item.title}
  //         >
  //           <Box ng-bind="item.title" />
  //         </ListItem>
  //       ))}
  //     {isMobile && (
  //       <>
  //         {userStore.isAdmin && (
  //           <>
  //             <ListItem
  //               className="hoverable black-text highlighted"
  //               onClick={() => openPage('/bulk')}
  //               aria-label="Import/Export Data"
  //             >
  //               Import/Export
  //             </ListItem>
  //             <ListItem
  //               className="hoverable black-text highlighted"
  //               onClick={() => openPage('/users')}
  //               aria-label="User Administration"
  //             >
  //               User Administration
  //             </ListItem>
  //           </>
  //         )}
  //         <ListItem
  //           className="hoverable black-text highlighted"
  //           onClick={() => openPage('/settings/profile')}
  //           aria-label="Edit User Profile"
  //         >
  //           Edit User Profile
  //         </ListItem>
  //         <ListItem
  //           className="hoverable black-text highlighted"
  //           onClick={() => openPage('/settings/password')}
  //           aria-label="Change User Password"
  //         >
  //           Change User Password
  //         </ListItem>
  //         <ListItem
  //           className="hoverable black-text"
  //           onClick={() => openPage('/auth/signout')}
  //           aria-label="Sign Out"
  //         >
  //           <strong>Sign Out</strong>
  //         </ListItem>
  //       </>
  //     )}
  //     {/*</md-content> */}
  //   </Box>
  //   {/*</md-sidenav>   */}
  //    </Box>
  //  </Box>
});
